/**
 * Shared error handling patterns for consistent user feedback
 */

import { toast } from '@/lib/toast';

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

/**
 * Shape of a typical API error response
 */
export interface ApiError {
  status: number;
  message?: string;
  code?: string;
  validationErrors?: Record<string, string[]>;
}

/**
 * Shape of a network/runtime error
 */
export interface RuntimeError {
  name?: string;
  message?: string;
  code?: string;
}

/**
 * Union type representing all possible error shapes we handle
 */
export type ErrorLike = ApiError | RuntimeError | Error | string | null;

/**
 * Error log entry with typed error
 */
interface ErrorLogEntry {
  timestamp: Date;
  error: ErrorLike;
  context?: ErrorContext;
}

/**
 * Parsed error result
 */
interface ParsedError {
  message: string;
  severity: ErrorSeverity;
  retryable: boolean;
}

/**
 * Callback that receives the current retry context
 */
type RetryCallback = (attempt: number, error: ErrorLike) => void;

/**
 * Predicate to determine if an error should be retried
 */
type ShouldRetryFn = (error: ErrorLike, attempt: number) => boolean;

export interface ErrorContext {
  operation: string;
  details?: Record<string, unknown>;
  retryable?: boolean;
  userAction?: string;
}

/**
 * Type guard to check if error has API error shape
 */
function hasStatus(error: unknown): error is { status: number } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  );
}

/**
 * Normalize error to a consistent object shape for type-safe access
 */
interface NormalizedError {
  message?: string;
  code?: string;
  status?: number;
  validationErrors?: Record<string, string[]>;
}

function toErrorObject(error: ErrorLike): NormalizedError {
  if (error === null) return {};
  if (typeof error === 'string') return { message: error };
  if (error instanceof Error)
    return { message: error.message, code: error.name };
  return error as NormalizedError;
}

/**
 * Standard error messages for common scenarios
 */
export const ERROR_MESSAGES = {
  // Network errors
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  TIMEOUT: 'Request timed out. Please try again.',
  SERVER_ERROR: 'Server error. Please try again later.',

  // Auth errors
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',

  // File errors
  FILE_TOO_LARGE: 'File is too large. Maximum size is {maxSize}.',
  INVALID_FILE_TYPE: 'Invalid file type. Accepted types: {acceptedTypes}.',
  UPLOAD_FAILED: 'Failed to upload file. Please try again.',
  DOWNLOAD_FAILED: 'Failed to download file. Please try again.',

  // Data errors
  NOT_FOUND: 'The requested item was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  DUPLICATE_ERROR: 'This item already exists.',

  // Generic
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
  OPERATION_FAILED: 'Operation failed. Please try again.'
} as const;

/**
 * Error handler class for consistent error processing
 */
export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorLog: ErrorLogEntry[] = [];

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Handle an error with appropriate user feedback
   */
  handle(error: ErrorLike, context?: ErrorContext): void {
    // Log the error
    this.errorLog.push({
      timestamp: new Date(),
      error,
      context
    });

    // Determine error message and severity
    const { message, severity, retryable } = this.parseError(error, context);

    // Show user feedback
    this.showFeedback(message, severity, retryable, context);

    // Report to monitoring (if configured)
    this.reportError(error, context);
  }

  /**
   * Parse error to determine message and severity
   */
  private parseError(error: ErrorLike, context?: ErrorContext): ParsedError {
    const errorObj = toErrorObject(error);

    // Network errors
    if (
      errorObj.code === 'NETWORK_ERROR' ||
      errorObj.message?.includes('network')
    ) {
      return {
        message: ERROR_MESSAGES.NETWORK_ERROR,
        severity: 'warning',
        retryable: true
      };
    }

    // Timeout errors
    if (errorObj.code === 'TIMEOUT' || errorObj.message?.includes('timeout')) {
      return {
        message: ERROR_MESSAGES.TIMEOUT,
        severity: 'warning',
        retryable: true
      };
    }

    // Auth errors
    if (errorObj.status === 401) {
      return {
        message: ERROR_MESSAGES.UNAUTHORIZED,
        severity: 'error',
        retryable: false
      };
    }

    if (errorObj.status === 403) {
      return {
        message: ERROR_MESSAGES.SESSION_EXPIRED,
        severity: 'error',
        retryable: false
      };
    }

    // Server errors
    if (errorObj.status !== undefined && errorObj.status >= 500) {
      return {
        message: ERROR_MESSAGES.SERVER_ERROR,
        severity: 'error',
        retryable: true
      };
    }

    // Not found
    if (errorObj.status === 404) {
      return {
        message: ERROR_MESSAGES.NOT_FOUND,
        severity: 'warning',
        retryable: false
      };
    }

    // Validation errors
    if (errorObj.status === 400 || errorObj.validationErrors) {
      return {
        message: errorObj.message || ERROR_MESSAGES.VALIDATION_ERROR,
        severity: 'warning',
        retryable: false
      };
    }

    // File-specific errors
    if (context?.operation?.includes('upload')) {
      return {
        message: errorObj.message || ERROR_MESSAGES.UPLOAD_FAILED,
        severity: 'error',
        retryable: true
      };
    }

    if (context?.operation?.includes('download')) {
      return {
        message: ERROR_MESSAGES.DOWNLOAD_FAILED,
        severity: 'error',
        retryable: true
      };
    }

    // Default
    return {
      message: errorObj.message || ERROR_MESSAGES.UNKNOWN_ERROR,
      severity: 'error',
      retryable: context?.retryable ?? false
    };
  }

  /**
   * Show user feedback based on error
   */
  private showFeedback(
    message: string,
    severity: ErrorSeverity,
    retryable: boolean,
    context?: ErrorContext
  ): void {
    // Build the feedback message
    let feedbackMessage = message;

    if (context?.userAction) {
      feedbackMessage += ` ${context.userAction}`;
    }

    // Show toast based on severity
    switch (severity) {
      case 'info':
        toast.info(feedbackMessage);
        break;
      case 'warning':
        toast.warning(feedbackMessage);
        break;
      case 'error':
      case 'critical':
        toast.error(feedbackMessage, {
          action: retryable
            ? {
                label: 'Retry',
                onClick: () => {
                  // Trigger retry if callback provided
                  const onRetry = context?.details?.onRetry;
                  if (typeof onRetry === 'function') {
                    onRetry();
                  }
                }
              }
            : undefined
        });
        break;
    }
  }

  /**
   * Report error to monitoring service
   */
  private reportError(error: ErrorLike, context?: ErrorContext): void {
    // In production, send to error monitoring service
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry, LogRocket, etc.
      console.error('Error reported:', { error, context });
    } else {
      // In development, log to console
      console.error('Error:', error);
      if (context) {
        console.error('Context:', context);
      }
    }
  }

  /**
   * Get recent errors for debugging
   */
  getRecentErrors(limit: number = 10): ErrorLogEntry[] {
    return this.errorLog.slice(-limit);
  }

  /**
   * Clear error log
   */
  clearErrorLog(): void {
    this.errorLog = [];
  }
}

/**
 * Convenience function for handling errors
 */
export function handleError(error: ErrorLike, context?: ErrorContext): void {
  ErrorHandler.getInstance().handle(error, context);
}

/**
 * Wrap an async function with error handling
 */
export function withErrorHandling<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  context: Omit<ErrorContext, 'details'>
): (...args: TArgs) => Promise<TReturn> {
  return async (...args: TArgs): Promise<TReturn> => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error as ErrorLike, {
        ...context,
        details: { args }
      });
      throw error;
    }
  };
}

/**
 * Helper accessor for the singleton ErrorHandler. Not a React hook —
 * returns plain callbacks bound to the singleton.
 */
export function getErrorHandlerHelpers() {
  const handler = ErrorHandler.getInstance();

  return {
    handleError: (error: ErrorLike, context?: ErrorContext) =>
      handler.handle(error, context),
    getRecentErrors: () => handler.getRecentErrors(),
    clearErrors: () => handler.clearErrorLog()
  };
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  const errorObj = toErrorObject(error as ErrorLike);
  return (
    errorObj.code === 'NETWORK_ERROR' ||
    errorObj.message?.includes('network') ||
    errorObj.message?.includes('Network') ||
    errorObj.code === 'NetworkError'
  );
}

/**
 * Check if error is an API error with status code
 */
export function isApiError(error: unknown): error is ApiError {
  return hasStatus(error);
}

/**
 * Get user-friendly error message
 */
export function getUserFriendlyError(error: unknown): string {
  const errorObj = toErrorObject(error as ErrorLike);
  return errorObj.message || ERROR_MESSAGES.UNKNOWN_ERROR;
}

/**
 * Retry a function with exponential backoff
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: {
    maxAttempts?: number;
    delayMs?: number;
    shouldRetry?: ShouldRetryFn;
    onRetry?: RetryCallback;
  } = {}
): Promise<T> {
  const { maxAttempts = 3, delayMs = 1000, shouldRetry, onRetry } = options;

  let lastError: ErrorLike = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as ErrorLike;

      // Check if we should retry
      if (shouldRetry && !shouldRetry(lastError, attempt)) {
        throw error;
      }

      if (attempt < maxAttempts) {
        const delay = delayMs * Math.pow(2, attempt - 1); // Exponential backoff
        onRetry?.(attempt, lastError);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}
